import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV_LINKS = [
    { label: "Community", href: "/#pillars", type: "anchor" },
    { label: "Environment", to: "/environment", type: "route" },
    { label: "Hackathon", to: "/hackathon", type: "route" },
    { label: "Events", to: "/events", type: "route" },
    { label: "FAQ", href: "/#faq", type: "anchor" },
];

export default function Header() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const onDarkHero =
        (pathname === "/" || pathname === "/hackathon") && !scrolled;
    const lightNav = pathname === "/environment" || scrolled || !onDarkHero;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [pathname]);

    const goHackathon = () => navigate("/hackathon");

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                lightNav
                    ? "backdrop-blur-xl bg-white/95 border-b border-black/8 shadow-sm shadow-black/5"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 h-16 md:h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="logo-link"
                    className={`font-display text-xl md:text-2xl font-black tracking-tight flex items-center gap-2 ${
                        lightNav ? "text-zinc-950" : "text-white"
                    }`}
                >
                    <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#E54D2E] pulse-dot" />
                    Build&nbsp;Bout<span className="text-[#E54D2E]">.</span>
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    {NAV_LINKS.map((link) =>
                        link.type === "route" ? (
                            <Link
                                key={link.to}
                                to={link.to}
                                data-testid={`nav-${link.label.toLowerCase()}`}
                                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                                    lightNav
                                        ? "text-zinc-700 hover:text-zinc-950 hover:bg-black/5"
                                        : "text-white/80 hover:text-white hover:bg-white/10"
                                }`}
                            >
                                {link.label}
                            </Link>
                        ) : (
                            <a
                                key={link.href}
                                href={link.href}
                                data-testid={`nav-${link.label.toLowerCase()}`}
                                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                                    lightNav
                                        ? "text-zinc-700 hover:text-zinc-950 hover:bg-black/5"
                                        : "text-white/80 hover:text-white hover:bg-white/10"
                                }`}
                            >
                                {link.label}
                            </a>
                        ),
                    )}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={goHackathon}
                        data-testid="header-join-btn"
                        className={`hidden md:inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm font-medium transition-colors group ${
                            lightNav
                                ? "bg-zinc-950 text-white hover:bg-[#E54D2E]"
                                : "bg-[#E54D2E] text-white hover:bg-white hover:text-zinc-950"
                        }`}
                    >
                        Register for Event
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                    <button
                        type="button"
                        onClick={() => setMobileOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        className={`md:hidden h-10 w-10 inline-flex items-center justify-center rounded-full ${
                            lightNav
                                ? "bg-zinc-950 text-white"
                                : "bg-white/10 border border-white/15 text-white"
                        }`}
                        aria-label="Toggle menu"
                    >
                        {mobileOpen ? (
                            <X className="h-5 w-5" />
                        ) : (
                            <Menu className="h-5 w-5" />
                        )}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div
                    data-testid="mobile-menu"
                    className={`md:hidden backdrop-blur-xl border-b ${
                        lightNav
                            ? "bg-white/95 border-black/5"
                            : "bg-zinc-950/95 border-white/10 text-white"
                    }`}
                >
                    <div className="px-6 py-4 flex flex-col gap-1">
                        {NAV_LINKS.map((link) =>
                            link.type === "route" ? (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    onClick={() => setMobileOpen(false)}
                                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                                    className={`py-3 text-base font-medium border-b last:border-none ${
                                        lightNav
                                            ? "text-zinc-800 border-black/5"
                                            : "text-white/90 border-white/10"
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            ) : (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    onClick={() => setMobileOpen(false)}
                                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                                    className={`py-3 text-base font-medium border-b last:border-none ${
                                        lightNav
                                            ? "text-zinc-800 border-black/5"
                                            : "text-white/90 border-white/10"
                                    }`}
                                >
                                    {link.label}
                                </a>
                            ),
                        )}
                        <button
                            type="button"
                            onClick={() => {
                                setMobileOpen(false);
                                goHackathon();
                            }}
                            data-testid="mobile-join-btn"
                            className="mt-3 h-11 rounded-full bg-zinc-950 text-white text-sm font-medium"
                        >
                            Register for Event
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
}
